import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import Box from '@material-ui/core/Box';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Text } from '@this/src/components/shared/ui/data_displays/typography/text';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { Button } from '@this/shared/ui/inputs/button';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { Fetcher } from '@this/src/util';
import type { MailBounceEntryResponse } from '@this/src/domain/mail_bounce';
import MailBounceEntry from '@this/src/domain/mail_bounce';
import { useConfirmModal } from '@this/shared/ui/feedbacks/modal/confirm_modal';

interface CancelMailBounceResponse {
  message: string;
  cancel_mail_bounces?: unknown[];
}

const MailBounce: React.FC = observer(() => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState<MailBounceEntry | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const confirmModal = useConfirmModal();

  const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  }, []);

  const searchBounce = useCallback(
    async ({ updateMessage = true, targetEmail }: { updateMessage?: boolean; targetEmail?: string } = {}) => {
      const normalizedEmail = (targetEmail ?? email).trim();
      if (!normalizedEmail) {
        setErrorMessage('メールアドレスを入力してください。');
        setMessage('');
        setResult(null);
        return;
      }

      setIsSearching(true);
      setErrorMessage('');
      if (updateMessage) {
        setMessage('');
      }
      setResult(null);
      try {
        const response = await Fetcher.get<{
          message: string;
          list_mail_bounces?: MailBounceEntryResponse[];
        }>('/arrangement/mail_bounces.json', {
          email: normalizedEmail
        });
        if (updateMessage) {
          setMessage(response.message);
        }
        setResult(response.list_mail_bounces?.[0] ? new MailBounceEntry(response.list_mail_bounces[0]) : null);
      } catch (error) {
        setErrorMessage('検索に失敗しました。再度お試しください。');
      } finally {
        setIsSearching(false);
      }
    },
    [email]
  );

  const cancelBounce = useCallback(
    async (targetEmail: string) => {
      setIsCanceling(true);
      setErrorMessage('');
      try {
        const response = await Fetcher.delete<CancelMailBounceResponse>('/arrangement/mail_bounces.json', {
          email: targetEmail
        });
        setMessage(response.message);
        setResult(null);
        await searchBounce({ updateMessage: false, targetEmail });
      } catch (error) {
        setErrorMessage('解除に失敗しました。再度お試しください。');
      } finally {
        setIsCanceling(false);
      }
    },
    [searchBounce]
  );

  const handleCancelBounce = useCallback(async () => {
    const targetEmail = (result?.email || email).trim();
    if (!targetEmail) return;

    const { result: confirmed } = await confirmModal.open({
      description: 'メール送信対象外から削除すると復活できません。削除しますか？',
      submitButtonLabel: '削除実行',
      color: 'danger'
    });
    if (!confirmed) return;

    await cancelBounce(targetEmail);
  }, [cancelBounce, confirmModal, email, result]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        searchBounce();
      }
    },
    [searchBounce]
  );

  const showLoading = isSearching || isCanceling;

  return (
    <>
      <Box m={2}>
        <Text level="body" bold>
          メール送信対象外ユーザーの確認 / 解除
        </Text>
      </Box>
      <Box display="flex" alignItems="center" m={2}>
        <Input
          value={email}
          onChange={handleEmailChange}
          onKeyDown={handleKeyDown}
          placeholder="メールアドレスを入力"
          style={{ width: '320px', marginRight: '12px' }}
        />
        <Button onClick={() => searchBounce()} disabled={isSearching}>
          検索
        </Button>
        <Button
          color="danger"
          style={{ marginLeft: '8px' }}
          disabled={!result || isCanceling}
          onClick={handleCancelBounce}
        >
          対象外解除
        </Button>
      </Box>
      {errorMessage && (
        <Box m={2}>
          <Text color="danger">{errorMessage}</Text>
        </Box>
      )}
      {message && (
        <Box m={2}>
          <Text>{message}</Text>
        </Box>
      )}
      {showLoading && (
        <Box m={2}>
          <Loading />
        </Box>
      )}
      {!showLoading && result && (
        <Box m={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ minWidth: '160px' }}>ユーザー名</TableCell>
                <TableCell style={{ minWidth: '200px' }}>組織</TableCell>
                <TableCell style={{ minWidth: '200px' }}>メールアドレス</TableCell>
                <TableCell style={{ minWidth: '160px' }}>対象外登録日時</TableCell>
                <TableCell>理由</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {result.userId}:{result.userName}
                </TableCell>
                <TableCell>
                  {result.organizationId}:{result.organizationName}
                </TableCell>
                <TableCell>{result.email}</TableCell>
                <TableCell>{result.bouncedAt.format('YYYY/MM/DD HH:mm:DD')}</TableCell>
                <TableCell>{result.reason}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </>
  );
});

export default MailBounce;
